import React, { useRef } from "react"
import { Box } from "@chakra-ui/react"
import EventResourcesView from "components/layout/EventResourcesView"
import SelectPrompt from "components/SelectPrompt"
import {
  AccountUser, DioboxEvent, EventCounts, EventGroup, GuestSummary, Segment, Table,
} from "sharedTypes"
import ListHeader from "./ScrollView/GuestListHeader"
import GuestList from "./ScrollView/GuestList"
import MultipleSelectActions from "./MultipleSelectActions"
import SelectedGuest from "./SelectedGuest"
import TableView from "./TableView/TableView"
import NoGuestsMatch from "./NoGuestsMatch"

interface GuestsViewProps {
  viewMode: "list" | "table"
  guestId: string
  fetchNextPage: () => void
  isLoading: boolean
  isFetchingNextPage: boolean
  onSearch: (search: string) => void
  searchString: string
  order: string
  allSelected: boolean
  toggleSelectAll: () => void
  guests: GuestSummary[]
  multipleSelectEnabled: boolean
  filtered: boolean
  filtersActive: boolean
  chipsActive: boolean
  onClearFilters: () => void
  counts: EventCounts
  currency: string
  enhanceEnabled: boolean
  groupsEnabled: boolean
  eventGroups: EventGroup[]
  segments: Segment[]
  tables: Table[]
  timezone: string
  event: DioboxEvent
  accountUsers: AccountUser[]
  sort: (order: string) => void
}

const GuestsView = ({
  viewMode, guestId, fetchNextPage, isLoading, isFetchingNextPage, onSearch,
  searchString, order, allSelected, toggleSelectAll, guests, multipleSelectEnabled,
  filtered, filtersActive, chipsActive, onClearFilters,
  eventGroups, segments, counts, tables, enhanceEnabled, groupsEnabled,
  currency, timezone, event, accountUsers, sort,
}: GuestsViewProps) => {
  const scrollRef = useRef<HTMLDivElement>(null)

  // Show the no-match UI whenever there's any filter (search OR chips OR
  // segment OR guest list) and the fetch returned zero. NoGuestsMatch
  // picks the appropriate message + actions based on which axes are on.
  const showNoMatch = !isLoading && (!!searchString || filtersActive) && guests.length === 0

  if (viewMode === "table") {
    return (
      <Box position="absolute" top={0} left={0} right={0} bottom={0}>
        <TableView
          tables={tables}
          guests={guests}
          order={order}
          allSelected={allSelected}
          filtered={filtered}
          filtersActive={filtersActive}
          chipsActive={chipsActive}
          toggleSelectAll={toggleSelectAll}
          eventGroups={eventGroups}
          segments={segments}
          counts={counts}
          onSetOrder={sort}
          loading={isLoading}
          loadingMore={isFetchingNextPage}
          event={event}
          timezone={timezone}
          onScrollEnd={fetchNextPage}
          searchString={searchString}
          onClearSearch={() => onSearch("")}
          onClearFilters={onClearFilters}
        />
      </Box>
    )
  }

  return (
    <EventResourcesView
      listWidth="md"
      onScrollEnd={fetchNextPage}
      scrollViewRef={scrollRef}
      listHeader={(
        <ListHeader
          loading={isLoading}
          onSearch={onSearch}
          searchString={searchString}
          order={order}
          onSetOrder={(value) => sort(value)}
          allSelected={allSelected}
          toggleSelectAll={toggleSelectAll}
        />
      )}
      emptyState={showNoMatch ? (
        <NoGuestsMatch
          query={searchString}
          filtersActive={filtersActive}
          chipsActive={chipsActive}
          onClearSearch={() => onSearch("")}
          onClearFilters={onClearFilters}
          layout="stacked"
        />
      ) : undefined}
      listItems={(
        <GuestList
          guests={guests}
          multipleSelectEnabled={multipleSelectEnabled}
          order={order}
          allSelected={allSelected}
          filtered={filtered}
          scrollRef={scrollRef}
        />
      )}
      showBorder={!!guestId}
      selectedItem={
        multipleSelectEnabled || allSelected
          ? (
            <MultipleSelectActions
              eventGroups={eventGroups}
              segments={segments}
              counts={counts}
              tables={tables}
            />
          )
          : (
            guestId
              ? (
                <SelectedGuest
                  enhanceEnabled={enhanceEnabled}
                  groupsEnabled={groupsEnabled}
                  currency={currency}
                  timezone={timezone}
                  tables={tables}
                  teamMembers={accountUsers}
                  event={event}
                />
              )
              : <SelectPrompt text="Select a Guest" />
          )
      }
    />
  )
}

export default GuestsView
